import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import HowTo1 from '../assets/HowTo1.jpeg';
import HowTo2 from '../assets/HowTo2.jpeg';
import HowTo3 from '../assets/HowTo3.jpeg';

const SectionContainer = styled.section`
  padding: 5rem 2rem;
  background: #f7fafc;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #2d3748;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #4a5568;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6rem;
  margin-top: 4rem;
`;

const Step = styled.div<{ reverse?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.reverse ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ImageContainer = styled.div`
  width: 400px; /* Bigger image size */
  height: 250px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  border: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -20px;
  left: ${props => (props.theme.reverse ? 'auto' : '0')};
  right: ${props => (props.theme.reverse ? '0' : 'auto')};
  background: #4299e1;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    position: static;
    margin-bottom: 1rem;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const steps = [
  {
    image: HowTo1,
    title: "Join a Wheel",
    description: "Easily find and join a networking wheel that matches your interests or event. Get ready to connect!"
  },
  {
    image: HowTo2,
    title: "Get Matched",
    description: "Our intelligent algorithm pairs you with relevant individuals for one-on-one video chats. No more awkward introductions."
  },
  {
    image: HowTo3,
    title: "Start Video Chat",
    description: "Engage in seamless video conversations, exchange ideas, and build valuable connections in real-time."
  }
];

export const UserHowItWorksPage: React.FC = () => {
  return (
    <SectionContainer>
      <Helmet>
        <title>How Spin To Connect Works for Users - Easy Networking Steps</title>
        <meta name="description" content="Learn how to use Spin To Connect in three simple steps: join a wheel, get matched, and start video chatting to expand your network." />
      </Helmet>
      <Content>
        <Header>
          <Title>How It Works for Users</Title>
          <Subtitle>
            Get started with SpinToConnect in three simple steps and transform
            your networking experience.
          </Subtitle>
        </Header>
        <StepsContainer>
          {steps.map((step, index) => (
            <Step key={index} reverse={index % 2 !== 0}>
              <ImageWrapper>
                <ImageContainer>
                  <img src={step.image} alt={step.title} />
                </ImageContainer>
                <StepNumber>{index + 1}</StepNumber>
              </ImageWrapper>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </Step>
          ))}
        </StepsContainer>
      </Content>
    </SectionContainer>
  );
};
